import { useId, useState } from 'react'
import type { JSX } from 'react'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  Sidebar,
  useSidebar,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import { useChannels } from '../ChannelsSection/useChannels'
import { LockedChannelItem } from './LockedChannelItem'

import styles from './LockedChannelsSection.module.css'

/**
 * Sidebar section listing the organization's locked channels. Locked channels
 * are otherwise only discoverable in Settings → Channels, so this surfaces them
 * in the primary nav. The section is read-only and collapsed by default; each
 * row opens the manage-channels dialog.
 */
export function LockedChannelsSection(): JSX.Element | null {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const { state } = useSidebar()
  const { lockedChannels } = useChannels()
  const [isExpanded, setIsExpanded] = useState(false)
  const contentId = useId()
  const commonTrackingProperties = useCommonTrackingProperties()

  // Only members who can manage the plan can act on locked channels, so the
  // section is pointless for everyone else.
  if (!canManageChannels) return null
  if (lockedChannels.length === 0) return null
  // The section is label-driven; hide it entirely in the icon-only rail.
  if (state !== 'expanded') return null

  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon

  const handleToggle = (): void => {
    const nextExpanded = !isExpanded
    setIsExpanded(nextExpanded)
    if (nextExpanded) {
      BufferTracker.cTAClicked({
        ...commonTrackingProperties,
        product: Product.Publish,
        cta: 'publish-mainSidebar-lockedChannelsExpand-1',
        clientName: 'publishWeb',
        upgradePathName: null,
      })
    }
  }

  return (
    <Sidebar.Group className={styles.lockedChannelsSection}>
      <Sidebar.GroupLabel>
        <button
          type="button"
          className={styles.header}
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          <span>Locked channels · {lockedChannels.length}</span>
          <ChevronIcon size="xsmall" aria-hidden className={styles.chevron} />
        </button>
      </Sidebar.GroupLabel>
      <Sidebar.GroupContent
        id={contentId}
        className={styles.groupContent}
        hidden={!isExpanded}
      >
        <Sidebar.List aria-label="Locked channels">
          {lockedChannels.map((channel) => (
            <Sidebar.ListItem key={channel.id}>
              <LockedChannelItem channel={channel} />
            </Sidebar.ListItem>
          ))}
        </Sidebar.List>
      </Sidebar.GroupContent>
    </Sidebar.Group>
  )
}
